import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { SectionHead, Reveal, Crosshair } from "./Reveal";
import { useLang } from "../../i18n/LanguageContext";
import { ArrowUpRight, Landmark } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = {
  en: ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"],
  sw: ["JAN", "FEB", "MAC", "APR", "MEI", "JUN", "JUL", "AGO", "SEP", "OKT", "NOV", "DES"],
  pl: ["STY", "LUT", "MAR", "KWI", "MAJ", "CZE", "LIP", "SIE", "WRZ", "PAŹ", "LIS", "GRU"],
};

const fmtRange = (start, end, abbrs) => {
  if (!start) return "";
  const a = new Date(`${start}T00:00:00`);
  const b = new Date(`${(end || start)}T00:00:00`);
  if (start === end || !end) return `${a.getDate()} ${abbrs[a.getMonth()]} ${a.getFullYear()}`;
  if (a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear())
    return `${a.getDate()}–${b.getDate()} ${abbrs[a.getMonth()]} ${a.getFullYear()}`;
  return `${a.getDate()} ${abbrs[a.getMonth()]} – ${b.getDate()} ${abbrs[b.getMonth()]} ${b.getFullYear()}`;
};

export const Financing = () => {
  const { t, lang } = useLang();
  const f = t.financing;
  const [openIdx, setOpenIdx] = useState(0);
  const [live, setLive] = useState(null);

  useEffect(() => {
    axios.get(`${API}/events`).then((r) => {
      if (Array.isArray(r.data?.events) && r.data.events.length) setLive(r.data.events);
    }).catch(() => {});
  }, []);

  const events = live
    ? live.map((e) => {
        const local = f.events.find((s) => s.url === e.url);
        return {
          name: e.name,
          url: e.url,
          venue: (e.location || "").replace(/, (Kenia|Kenya|Tanzania|Uganda)$/i, ""),
          dates: fmtRange(e.startDate, e.endDate, MONTHS[lang] || MONTHS.en),
          desc: local ? local.desc : lang === "en" ? e.tagline_en || e.tagline : lang === "sw" ? e.tagline_sw || e.tagline : e.tagline,
          joined: e.delegationCount,
        };
      })
    : f.events;
  return (
    <section id="financing" className="relative bg-canvas bp-grid py-24 sm:py-32 border-b border-technical" data-testid="financing-section">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8">
        <div className="flex items-start gap-5">
          <Reveal>
            <div className="chamfer-sm hidden sm:flex h-14 w-14 shrink-0 items-center justify-center border border-[rgba(122,22,32,0.35)] bg-white">
              <Landmark className="h-6 w-6 text-red-brand" />
            </div>
          </Reveal>
          <SectionHead eyebrow={f.eyebrow} title={f.title} desc={f.desc} testid="financing" />
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-[rgba(22,24,27,0.12)] border border-technical">
          {f.cards.map((card, i) => (
            <Reveal key={card.title} delay={i * 0.08} className="relative bg-canvas p-8 sm:p-10 group transition-colors duration-300 hover:bg-white">
              <Crosshair className="top-3 right-3 opacity-40 group-hover:opacity-100 transition-opacity duration-300" />
              <span className="font-mono-tech text-4xl font-bold text-[#16181b]/10 group-hover:text-red-brand transition-colors duration-300 leading-none">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="mt-4 font-display text-xl sm:text-2xl font-bold tracking-tight text-ink" data-testid={`financing-card-${i}`}>
                {card.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-[#525964] max-w-md">{card.desc}</p>
              {card.linkUrl && (
                <a
                  data-testid={`financing-link-${i}`}
                  href={card.linkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="chamfer-sm mt-5 inline-flex items-center gap-1.5 border border-technical bg-white px-3 py-1.5 font-mono-tech text-[10px] font-semibold tracking-[0.15em] text-[#525964] transition-colors duration-200 hover:border-red-brand hover:text-red-brand"
                >
                  {card.linkLabel}
                  <ArrowUpRight className="h-3 w-3" />
                </a>
              )}
            </Reveal>
          ))}
        </div>
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <Reveal>
              <p className="eyebrow" data-testid="financing-faq-eyebrow">{f.faqEyebrow}</p>
            </Reveal>
            <div className="mt-6 border border-technical bg-white">
              {f.faq.map((item, i) => {
                const open = openIdx === i;
                return (
                  <div key={item.q} className="border-b border-technical last:border-b-0">
                    <button
                      data-testid={`faq-toggle-${i}`}
                      onClick={() => setOpenIdx(open ? -1 : i)}
                      className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                    >
                      <span className="font-display text-sm sm:text-base font-bold tracking-tight text-ink">{item.q}</span>
                      <span className={`font-mono-tech text-red-brand text-lg leading-none transition-transform duration-300 ${open ? "rotate-45" : ""}`}>+</span>
                    </button>
                    <AnimatePresence initial={false}>
                      {open && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                          className="overflow-hidden"
                        >
                          <p data-testid={`faq-answer-${i}`} className="px-5 pb-5 text-sm leading-relaxed text-[#525964]">{item.a}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>
          <div>
            <Reveal>
              <div className="flex flex-wrap items-center gap-3">
                <p className="eyebrow" data-testid="financing-events-eyebrow">{f.eventsEyebrow}</p>
                {live && (
                  <span
                    data-testid="events-live-badge"
                    className="flex items-center gap-1.5 border border-[rgba(122,22,32,0.3)] px-2 py-1 font-mono-tech text-[9px] uppercase tracking-[0.15em] text-red-brand"
                  >
                    <span className="pulse-dot inline-block h-1.5 w-1.5 rounded-full bg-red-brand" />
                    {f.liveBadge}
                  </span>
                )}
              </div>
            </Reveal>
            <div className="mt-6 space-y-4">
              {events.map((e, i) => (
                <Reveal key={e.name} delay={i * 0.06}>
                  <a
                    data-testid={`event-card-${i}`}
                    href={e.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-start justify-between gap-4 border border-technical bg-white p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-[rgba(122,22,32,0.4)] hover:shadow-[0_14px_32px_-16px_rgba(22,24,27,0.3)]"
                  >
                    <div>
                      <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-red-brand">{e.dates}</p>
                      <h4 className="mt-1.5 font-display text-base font-bold tracking-tight text-ink transition-colors duration-200 group-hover:text-red-brand">{e.name}</h4>
                      <p className="mt-1 font-mono-tech text-[10px] tracking-[0.12em] text-[#9ca3af]">{e.venue}</p>
                      {e.joined > 0 && (
                        <span className="mt-2 inline-block bg-sand px-2 py-0.5 font-mono-tech text-[9px] uppercase tracking-[0.12em] text-[#525964]">
                          {e.joined} {f.joinedForms ? f.joinedForms[e.joined === 1 ? 0 : (e.joined % 10 >= 2 && e.joined % 10 <= 4 && (e.joined % 100 < 12 || e.joined % 100 > 14)) ? 1 : 2] : f.joined}
                        </span>
                      )}
                      <p className="mt-2 text-xs leading-relaxed text-[#525964]">{e.desc}</p>
                    </div>
                    <ArrowUpRight className="h-4 w-4 shrink-0 text-[#9ca3af] transition-colors duration-200 group-hover:text-red-brand" />
                  </a>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.2}>
              <div className="mt-6 flex flex-wrap items-center gap-4 border border-dashed border-[rgba(122,22,32,0.4)] bg-white px-5 py-4">
                <p className="min-w-[220px] flex-1 text-xs leading-relaxed text-[#525964]">{f.eventsNote}</p>
                <a
                  data-testid="events-cta"
                  href="https://path.com.pl"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="chamfer-sm bg-red-brand px-4 py-2.5 font-mono-tech text-[10px] font-semibold uppercase tracking-[0.15em] text-[#f8f7f4] transition-colors duration-200 hover:bg-[#5f1019]"
                >
                  {f.eventsCta}
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
